import React, { useEffect, useRef, useState, useMemo } from 'react';
import classNames from 'classnames';
import { useSearchParams } from 'react-router-dom';
import { VideoJsPlayer } from 'video.js';
import { useAssetMVPermissions } from '@hooks';
import {
  AudioVideoMetadataDto,
  DocumentMetadataDto,
  ImageMetadataDto,
  AssetItemDto,
  AssetVersionItemDto
} from '@api/Api';
import { checkCanPlaySource } from '@helpers/checkCanPlaySource';
import CollaborateOnAnAssetModal from '@components/Modals/BootcampModal/components/CollaborateOnAnAssetModal/CollaborateOnAnAssetModal';
import ShapesDrawingTools from '@pages/MediaViewer/AssetViewer/ShapesDrawingTools';
import { LgWidth } from '@components/Responsive';
import MediaViewerMobileAction, {
  useMediaViewerMobileActionVisibility
} from '@pages/MediaViewer/components/MediaViewerMobileAction/MediaViewerMobileAction';
import useMediaViewerReviewAssetTour from '@hooks/tour/useMediaViewerReviewAssetTour';
import useMediaViewerCreateTaskFromComment from '@hooks/tour/useMediaViewerCreateTaskFromComment';
import useMediaViewerTextHighlightTour from '@hooks/tour/useMediaViewerTextHighlightTour';
import useMediaViewerReviewAudioVideoTour from '@hooks/tour/useMediaViewerReviewAudioVideoTour';
import useMediaViewerAnnouncementTour from '@hooks/tour/useMediaViewerAnnouncementTour';
import { CURSOR_DRAW_TOOL } from '@pages/MediaViewer/helpers/drawToolShape';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { MediaViewerUnsupportedIcon } from '@icons';
import { clearLastShownMedia } from '@pages/MediaViewer/AssetViewer/lastShownMedia';

type AssetViewerProps = {
  isShowShapeDrawingTools?: boolean;
  setSelectedCollapse?: (collapse: string | null) => void;
  isDesktopSidebarOpen?: boolean;
  asset: AssetItemDto;
  selectedVersion: AssetVersionItemDto;
  type: 'main' | 'left' | 'right';
  isEmbeddedWebView?: boolean;
  mobileAction?: {
    commentMentions: boolean;
    canCreateTask: boolean;
    isShowFloatCommentForm: boolean;
    isShowFloatCommentItem: boolean;
  };
};

const PdfCard = React.lazy(
  () => import('@pages/MediaViewer/AssetViewer/PdfCard')
);
const ImageCard = React.lazy(
  () => import('@pages/MediaViewer/AssetViewer/ImageCard')
);
const VideoCard = React.lazy(
  () => import('@pages/MediaViewer/AssetViewer/VideoCard')
);
const WebsiteCard = React.lazy(
  () => import('@pages/MediaViewer/AssetViewer/WebsiteCard')
);

function isWebsiteVersion(
  asset: AssetVersionItemDto | undefined | null
): asset is AssetVersionItemDto & { metadata: { type: 'website' } } {
  return asset?.metadata.type === 'website';
}

function AssetViewer({
  isShowShapeDrawingTools = true,
  setSelectedCollapse,
  isDesktopSidebarOpen = false,
  isEmbeddedWebView = false,
  mobileAction,
  asset,
  selectedVersion,
  type: viewerType
}: AssetViewerProps) {
  const { setDrawToolsShape } = useAssetViewerState();
  const canLeaveComment = useAssetMVPermissions('comment');

  useEffect(() => {
    if (!canLeaveComment) {
      setDrawToolsShape(CURSOR_DRAW_TOOL);
    }
  }, [canLeaveComment, setDrawToolsShape]);

  // Mounted for every asset: once the card has taken the previous asset's
  // image or frame (in its layout effects, before this effect), drop it
  // whatever this asset is (e.g. a document), so it does not come back later
  useEffect(() => {
    clearLastShownMedia();
  }, []);

  const assetType = useMemo(() => {
    return selectedVersion?.metadata.type;
  }, [selectedVersion?.id]);
  const isImage =
    assetType === 'raster_image' ||
    assetType === 'camera_raw_image' ||
    assetType === 'vector_image';
  const isDocument =
    assetType === 'document' ||
    assetType === 'presentation' ||
    assetType === 'spreadsheet';
  const isVideo = assetType === 'video';
  const isAudio = assetType === 'audio';
  const isWebsite = isWebsiteVersion(selectedVersion);

  const [isUnsupported, setIsUnsupported] = useState<boolean>(false);
  const [isPreparing, setIsPreparing] = useState<boolean>(false);
  const [isContainerExpand, setIsContainerExpand] = useState<boolean>(false);
  const [isAssetReady, setIsAssetReady] = useState<boolean>(false);
  const [collaborateOnAnAssetModal, setCollaborateOnAnAssetModal] =
    useState<boolean>(false);

  const [searchParams] = useSearchParams();

  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  useEffect(() => {
    if (!selectedVersion) return;
    if ((selectedVersion.metadata.type as any) === 'unknown') {
      setIsUnsupported(true);
      return;
    }
    if (isVideo) {
      const metadata = selectedVersion.metadata as AudioVideoMetadataDto;
      const videoProxies = Object.entries(metadata.videoProxies).map(
        (el) => el[1]
      );
      const playableVideoProxies = videoProxies.filter(
        (x) => x.status === 'ready'
      );
      if (checkCanPlaySource(selectedVersion)) {
        playableVideoProxies.push(selectedVersion?.sourceUrl);
      }
      const isAnyPending = videoProxies.some((x) => x.status === 'pending');

      if (!playableVideoProxies.length) {
        if (isAnyPending) {
          setIsPreparing(true);
          return;
        }
        setIsUnsupported(true);
      } else {
        setIsPreparing(false);
        setIsUnsupported(false);
      }
    }
    if (isAudio) {
      const metadata = selectedVersion.metadata as AudioVideoMetadataDto;
      if (metadata.audioProxy.status === 'pending') {
        setIsPreparing(true);
        return;
      }
      if (metadata.audioProxy.status === 'ready') {
        setIsPreparing(false);
        setIsUnsupported(false);
        return;
      }
      setIsUnsupported(true);
    }
    if (isDocument || isImage) {
      const metadata = selectedVersion.metadata as
        | ImageMetadataDto
        | DocumentMetadataDto;
      if (metadata.proxy.status === 'pending') setIsPreparing(true);
      else if (metadata.proxy.status === 'ready') {
        setIsPreparing(false);
        setIsUnsupported(false);
      } else setIsUnsupported(true);
    }
  }, [selectedVersion?.metadata, selectedVersion?.id]);

  useEffect(() => {
    const startTour = searchParamsRef.current.get('startTour') ?? undefined;
    if (!asset?.workspaceId) return;
    if (!startTour) return;
    if (isAudio || isUnsupported) setCollaborateOnAnAssetModal(true);
  }, [asset?.workspaceId, isAudio, isUnsupported]);

  useMediaViewerReviewAssetTour({
    isAssetReady,
    isAudio,
    isPreparing,
    isUnsupported,
    assetWorkspaceId: asset?.workspaceId,
    setSelectedCollapse
  });

  useMediaViewerCreateTaskFromComment({
    isAssetReady,
    isPreparing,
    isUnsupported,
    setSelectedCollapse
  });

  useMediaViewerTextHighlightTour({
    isAssetReady,
    isPreparing,
    isUnsupported,
    isDocument,
    setSelectedCollapse
  });

  useMediaViewerReviewAudioVideoTour({
    isAssetReady,
    isPreparing,
    isUnsupported,
    setSelectedCollapse,
    isAudio: isVideo || isAudio
  });

  useMediaViewerAnnouncementTour({
    isAssetReady,
    isPreparing,
    isUnsupported,
    setSelectedCollapse
  });

  const mobileActionVisibility = useMediaViewerMobileActionVisibility({
    isShowFloatCommentForm: mobileAction?.isShowFloatCommentForm ?? false,
    isShowFloatCommentItem: mobileAction?.isShowFloatCommentItem ?? false
  });

  const playerRef = useRef<VideoJsPlayer>(null);
  const handleAssetReady = () => {
    setIsAssetReady(true);
  };
  const handleAssetError = () => {
    setIsUnsupported(true);
  };

  if (isUnsupported) {
    return (
      <>
        <div
          className={classNames(
            isUnsupported && 'media_viewer_asset-unsupported',
            'media_viewer_asset'
          )}
        >
          <MediaViewerUnsupportedIcon />
          Preview unsupported
        </div>
        {asset?.workspaceId && (
          <CollaborateOnAnAssetModal
            workspaceId={asset.workspaceId}
            setVisible={setCollaborateOnAnAssetModal}
            visible={collaborateOnAnAssetModal}
          />
        )}
      </>
    );
  }

  const shapesClassName = classNames(
    isVideo && 'b-media_viewer_video',
    isAudio && 'b-media_viewer_audio',
    isDocument && 'b-media_viewer_document',
    isContainerExpand && 'b-media_viewer_asset-expanded'
  );
  const showShapeDrawingTools = isShowShapeDrawingTools && !isEmbeddedWebView;

  return (
    <>
      {canLeaveComment && showShapeDrawingTools && (
        <LgWidth>
          <ShapesDrawingTools
            player={playerRef.current}
            className={shapesClassName}
          />
        </LgWidth>
      )}
      {mobileAction && mobileActionVisibility && (
        <MediaViewerMobileAction
          visibility={mobileActionVisibility}
          {...mobileAction}
          asset={asset}
          selectedVersion={selectedVersion}
          isShowShapeDrawingTools={isShowShapeDrawingTools}
          isContainerExpand={isContainerExpand}
          setSelectedCollapse={setSelectedCollapse}
        />
      )}
      <div
        className={classNames(
          isVideo && 'media_viewer_video',
          isAudio && 'media_viewer_audio',
          isDocument && 'media_viewer_document',
          isContainerExpand && 'media_viewer_asset-expanded',
          'media_viewer_asset'
        )}
      >
        {(isVideo || isAudio) && selectedVersion && (
          <VideoCard
            ref={playerRef}
            asset={selectedVersion}
            onError={handleAssetError}
            onReady={handleAssetReady}
            isAssetReady={isAssetReady}
            isPreparing={isPreparing}
            setSelectedCollapse={setSelectedCollapse}
          />
        )}
        {isDocument && selectedVersion && (
          <PdfCard
            asset={selectedVersion}
            onReady={handleAssetReady}
            isExpand={isContainerExpand}
            setIsExpand={setIsContainerExpand}
            isPreparing={isPreparing}
            isAssetReady={isAssetReady}
            mobileActionVisibility={mobileActionVisibility}
          />
        )}
        {isImage && selectedVersion && (
          <ImageCard
            asset={selectedVersion}
            onReady={handleAssetReady}
            isExpand={isContainerExpand}
            setIsExpand={setIsContainerExpand}
            isPreparing={isPreparing}
            isAssetReady={isAssetReady}
            mobileActionVisibility={mobileActionVisibility}
          />
        )}
        {isWebsite && selectedVersion && (
          <WebsiteCard
            assetId={asset.id}
            assetVersion={selectedVersion}
            isDesktopSidebarOpen={isDesktopSidebarOpen}
            viewerType={viewerType}
          />
        )}
      </div>
    </>
  );
}

export default AssetViewer;
